import type { ModuleId } from "../types";

interface InputPair {
  doseId: string;
  rateId: string;
}

export interface PagerTargetModule {
  id: ModuleId;
  /** null na cauda — o pager mostra só o título. */
  number: number | null;
  title: string;
}

export interface PagerConfig {
  prev?: PagerTargetModule;
  next?: PagerTargetModule;
  onNavigate: (moduleId: ModuleId) => void;
}

export interface CollapseConfig {
  /** IDs de section que iniciam ABERTAS. Quando definido, as demais iniciam fechadas.
   *  Em accordionMode, esses IDs ficam SEMPRE abertos e SEM toggle (sempre visiveis).
   *  Se omitido ou vazio, todas iniciam abertas (usuario ainda pode recolher). */
  keepExpandedIds?: string[];
  /** Se true, NENHUMA section inicia aberta: o modulo abre como indice puro.
   *  Para modulos que reunem varios assuntos independentes, onde abrir um deles
   *  sugere que o modulo e sobre ele. Existe como flag, e nao como id que nao casa,
   *  porque id fantasma funciona por acidente e ninguem entende depois. */
  startAllCollapsed?: boolean;
  /** Se true, pula sections que ja contem <details> nativos para evitar toggles
   *  redundantes/aninhados (ex: Modulo de Disturbios). */
  skipSectionsWithDetails?: boolean;
  /** Se true, abrir uma section recolhe automaticamente as demais (comportamento
   *  acordeao). Sections em keepExpandedIds ficam imutaveis (sempre abertas, sem
   *  participar do acordeao). */
  accordionMode?: boolean;
}

export interface SafetyLayerOptions {
  pager?: PagerConfig;
  collapse?: CollapseConfig;
  /** Aplica passada de compactacao tipografica (corpo do texto, sections, headings)
   *  para reduzir scroll em modulos clinicos densos. Nao tocar em tabelas (que ja
   *  tem estilo proprio) nem em paineis de calculadora. */
  compactReading?: boolean;
}

const PAGER_ID = "codex-module-pager";
const PAGER_STYLE_ID = "codex-module-pager-style";
const COLLAPSE_STYLE_ID = "codex-section-collapse-style";
const COLLAPSE_BODY_CLASS = "codex-section-body";
const COLLAPSE_TOGGLE_CLASS = "codex-section-toggle";
const COLLAPSE_COLLAPSED_CLASS = "codex-section-collapsed";
const MOBILE_STYLE_ID = "codex-mobile-responsive-style";
const COMPACT_READING_STYLE_ID = "codex-compact-reading-style";
const TABLE_MODAL_ID = "codex-table-modal";
const TABLE_MODAL_STYLE_ID = "codex-table-modal-style";
const TABLE_LABELS_FLAG = "codexLabelsApplied";

const DOSE_RATE_PAIRS: InputPair[] = [
  { doseId: "dvaDose", rateId: "dvaRate" },
  { doseId: "infDose", rateId: "infRate" }
];

function asInput(element: HTMLElement | null): HTMLInputElement | null {
  return element instanceof HTMLInputElement ? element : null;
}

function originalAriaLabel(input: HTMLInputElement): string {
  if (input.dataset.codexOriginalAriaLabel === undefined) {
    input.dataset.codexOriginalAriaLabel = input.getAttribute("aria-label") ?? "";
  }

  return input.dataset.codexOriginalAriaLabel;
}

function setGuardState(active: HTMLInputElement, calculated: HTMLInputElement): void {
  const hasActiveValue = active.value.trim().length > 0;
  const originalLabel = originalAriaLabel(calculated);

  calculated.disabled = hasActiveValue;
  calculated.classList.toggle("codex-calculated-field", hasActiveValue);

  if (hasActiveValue) {
    calculated.setAttribute("aria-label", "Campo bloqueado automaticamente enquanto o campo oposto esta em uso");
  } else if (originalLabel) {
    calculated.setAttribute("aria-label", originalLabel);
  } else {
    calculated.removeAttribute("aria-label");
  }
}

function wirePair(document: Document, pair: InputPair): void {
  const dose = asInput(document.getElementById(pair.doseId));
  const rate = asInput(document.getElementById(pair.rateId));

  if (!dose || !rate || dose.dataset.codexGuarded === "true") {
    return;
  }

  const sync = () => {
    if (dose.value.trim() && rate.value.trim()) {
      rate.value = "";
    }
    setGuardState(dose, rate);
    setGuardState(rate, dose);
  };

  dose.dataset.codexGuarded = "true";
  rate.dataset.codexGuarded = "true";
  dose.addEventListener("input", sync);
  rate.addEventListener("input", sync);
  sync();
}

function injectGuardStyles(document: Document): void {
  if (document.getElementById("codex-runtime-safety-style")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "codex-runtime-safety-style";
  style.textContent = `
    .codex-calculated-field {
      opacity: .68;
      cursor: not-allowed;
      background: #eef3f8 !important;
      /* Fundo é forçado claro; garantir texto ESCURO para não dar claro-em-claro
         no tema dark (defensivo — ver ERR-019/L-25). */
      color: #172033 !important;
    }
  `;
  document.head.appendChild(style);
}

function removeInternalModuleMenu(document: Document): void {
  if (!document.getElementById("codex-runtime-module-menu-style")) {
    const style = document.createElement("style");
    style.id = "codex-runtime-module-menu-style";
    style.textContent = `
      .app {
        display: block !important;
        grid-template-columns: 1fr !important;
      }
      .app > aside {
        display: none !important;
      }
      .app > main,
      main {
        max-width: none !important;
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);
  }

  document.querySelector<HTMLElement>(".app > aside")?.remove();
  document.querySelector<HTMLElement>(".app")?.classList.add("codex-module-menu-removed");
}

function injectPagerStyles(doc: Document): void {
  if (doc.getElementById(PAGER_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = PAGER_STYLE_ID;
  style.textContent = `
    .codex-pager {
      display: grid;
      gap: 12px;
      grid-template-columns: 1fr 1fr;
      margin: 40px 0 24px;
    }
    @media (max-width: 720px) { .codex-pager { grid-template-columns: 1fr; } }
    .codex-pager__btn {
      background: rgba(18,190,209,.06);
      border: 1px solid rgba(18,190,209,.22);
      border-radius: 14px;
      box-shadow: 0 4px 16px rgba(0,0,0,.24);
      color: #e8f4f8;
      cursor: pointer;
      display: grid;
      font: inherit;
      gap: 6px;
      padding: 18px 22px;
      text-align: left;
      text-decoration: none;
      transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
    }
    .codex-pager__btn:hover,
    .codex-pager__btn:focus-visible {
      border-color: rgba(18,190,209,.55);
      background: rgba(18,190,209,.12);
      box-shadow: 0 8px 24px rgba(18,190,209,.14);
      outline: 0;
      transform: translateY(-1px);
    }
    .codex-pager__btn--next { text-align: right; }
    .codex-pager__btn--placeholder {
      background: transparent;
      border: 1px dashed transparent;
      box-shadow: none;
      pointer-events: none;
      visibility: hidden;
    }
    .codex-pager__label {
      color: rgba(207,238,243,.65);
      font-size: 12.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .codex-pager__title {
      color: #e8f4f8;
      font-size: 17px;
      font-weight: 700;
      line-height: 1.3;
    }
  `;
  doc.head.appendChild(style);
}

function buildPagerButton(
  doc: Document,
  kind: "prev" | "next",
  target: PagerTargetModule,
  onClick: () => void
): HTMLButtonElement {
  const btn = doc.createElement("button");
  btn.type = "button";
  btn.className = `codex-pager__btn codex-pager__btn--${kind}`;
  btn.setAttribute(
    "aria-label",
    kind === "prev"
      ? `Voltar ao Módulo ${target.number} — ${target.title}`
      : `Passar para o Módulo ${target.number} — ${target.title}`
  );

  const label = doc.createElement("span");
  label.className = "codex-pager__label";
  label.textContent = kind === "prev" ? "← Módulo anterior" : "Próximo módulo →";

  const title = doc.createElement("span");
  title.className = "codex-pager__title";
  title.textContent = `Módulo ${target.number} — ${target.title}`;

  btn.appendChild(label);
  btn.appendChild(title);
  btn.addEventListener("click", onClick);

  return btn;
}

function injectModulePager(doc: Document, cfg: PagerConfig): void {
  doc.getElementById(PAGER_ID)?.remove();

  const pager = doc.createElement("nav");
  pager.id = PAGER_ID;
  pager.className = "codex-pager";
  pager.setAttribute("aria-label", "Navegacao entre modulos");

  if (cfg.prev) {
    const prev = cfg.prev;
    pager.appendChild(
      buildPagerButton(doc, "prev", prev, () => cfg.onNavigate(prev.id))
    );
  } else {
    const placeholder = doc.createElement("span");
    placeholder.className = "codex-pager__btn codex-pager__btn--placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    pager.appendChild(placeholder);
  }

  if (cfg.next) {
    const next = cfg.next;
    pager.appendChild(
      buildPagerButton(doc, "next", next, () => cfg.onNavigate(next.id))
    );
  } else {
    const placeholder = doc.createElement("span");
    placeholder.className = "codex-pager__btn codex-pager__btn--placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    pager.appendChild(placeholder);
  }

  // Inserir antes do footer-sign (ou ao final do main, se nao houver footer)
  const main = doc.querySelector("main");
  const footer = doc.querySelector(".footer-sign");
  if (main && footer && footer.parentElement === main) {
    main.insertBefore(pager, footer);
  } else if (main) {
    main.appendChild(pager);
  } else {
    doc.body.appendChild(pager);
  }
}

function injectCollapseStyles(doc: Document): void {
  if (doc.getElementById(COLLAPSE_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = COLLAPSE_STYLE_ID;
  style.textContent = `
    .${COLLAPSE_TOGGLE_CLASS} {
      align-items: baseline;
      cursor: pointer;
      display: flex;
      gap: 12px;
      user-select: none;
    }
    .${COLLAPSE_TOGGLE_CLASS}:focus-visible {
      outline: 2px solid #0b6b5c;
      outline-offset: 4px;
      border-radius: 4px;
    }
    .${COLLAPSE_TOGGLE_CLASS}-chevron {
      align-items: center;
      color: #0b6b5c;
      display: inline-flex;
      flex-shrink: 0;
      height: 24px;
      justify-content: center;
      transition: transform 200ms ease;
      width: 24px;
    }
    .${COLLAPSE_TOGGLE_CLASS}-chevron svg {
      fill: none;
      height: 16px;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 2.4;
      width: 16px;
    }
    .${COLLAPSE_COLLAPSED_CLASS} .${COLLAPSE_TOGGLE_CLASS}-chevron {
      transform: rotate(-90deg);
    }
    .${COLLAPSE_BODY_CLASS} {
      display: block;
    }
    .${COLLAPSE_COLLAPSED_CLASS} .${COLLAPSE_BODY_CLASS} {
      display: none;
    }
    .${COLLAPSE_COLLAPSED_CLASS} {
      padding-bottom: 12px;
    }
  `;
  doc.head.appendChild(style);
}

function makeSectionCollapsible(
  doc: Document,
  section: HTMLElement,
  startOpen: boolean,
  closeOthers?: (current: HTMLElement) => void
): void {
  const h2 = section.querySelector(":scope > h2");
  if (!h2 || h2.classList.contains(COLLAPSE_TOGGLE_CLASS)) return;

  // Move tudo que vem depois do h2 para um wrapper .codex-section-body
  let body = section.querySelector<HTMLElement>(`:scope > .${COLLAPSE_BODY_CLASS}`);
  if (!body) {
    body = doc.createElement("div");
    body.className = COLLAPSE_BODY_CLASS;
    let node: ChildNode | null = h2.nextSibling;
    while (node) {
      const next: ChildNode | null = node.nextSibling;
      body.appendChild(node);
      node = next;
    }
    section.appendChild(body);
  }

  // Adiciona chevron e wireup no h2
  h2.classList.add(COLLAPSE_TOGGLE_CLASS);
  h2.setAttribute("role", "button");
  h2.setAttribute("tabindex", "0");
  h2.setAttribute("aria-expanded", String(startOpen));
  if (section.id) {
    body.id = `${section.id}__body`;
    h2.setAttribute("aria-controls", body.id);
  }

  if (!h2.querySelector(`.${COLLAPSE_TOGGLE_CLASS}-chevron`)) {
    const chevron = doc.createElement("span");
    chevron.className = `${COLLAPSE_TOGGLE_CLASS}-chevron`;
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML = `<svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>`;
    h2.insertBefore(chevron, h2.firstChild);
  }

  if (!startOpen) {
    section.classList.add(COLLAPSE_COLLAPSED_CLASS);
  }

  const toggle = () => {
    const wasCollapsed = section.classList.contains(COLLAPSE_COLLAPSED_CLASS);
    // Quando esta abrindo (estava colapsada), fechar as demais primeiro (acordeao).
    if (wasCollapsed && closeOthers) {
      closeOthers(section);
    }
    const isCollapsed = section.classList.toggle(COLLAPSE_COLLAPSED_CLASS);
    h2.setAttribute("aria-expanded", String(!isCollapsed));
    if (!isCollapsed) {
      // Acabou de abrir — garantir que rola para a section aberta (UX no acordeao).
      section.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  };

  h2.addEventListener("click", toggle);
  h2.addEventListener("keydown", (event) => {
    const ke = event as KeyboardEvent;
    if (ke.key === "Enter" || ke.key === " ") {
      ke.preventDefault();
      toggle();
    }
  });
}

function injectSectionCollapse(doc: Document, cfg: CollapseConfig): void {
  injectCollapseStyles(doc);
  const sections = Array.from(
    doc.querySelectorAll<HTMLElement>("main > section[id]")
  );
  if (sections.length === 0) return;

  const keepOpen = new Set(cfg.keepExpandedIds ?? []);
  const hasExpandedFilter = keepOpen.size > 0 || cfg.startAllCollapsed === true;
  const accordionMode = cfg.accordionMode === true;

  // Em accordionMode, sections em keepExpandedIds NAO viram colapsiveis (sempre visiveis).
  // Nos outros modos, elas continuam sendo colapsiveis mas iniciam abertas.
  const collapsibleSections: HTMLElement[] = [];
  for (const section of sections) {
    if (cfg.skipSectionsWithDetails && section.querySelector(":scope > details")) {
      continue;
    }
    if (accordionMode && keepOpen.has(section.id)) {
      continue;
    }
    collapsibleSections.push(section);
  }

  const closeOthers = accordionMode
    ? (currentSection: HTMLElement) => {
        for (const other of collapsibleSections) {
          if (other === currentSection) continue;
          if (other.classList.contains(COLLAPSE_COLLAPSED_CLASS)) continue;
          other.classList.add(COLLAPSE_COLLAPSED_CLASS);
          const otherH2 = other.querySelector(":scope > h2");
          if (otherH2) otherH2.setAttribute("aria-expanded", "false");
        }
      }
    : undefined;

  for (const section of collapsibleSections) {
    // accordionMode: todas iniciam fechadas. Senao: somente as fora de keepExpandedIds iniciam fechadas.
    const startOpen = accordionMode
      ? false
      : hasExpandedFilter
        ? keepOpen.has(section.id)
        : true;
    makeSectionCollapsible(doc, section, startOpen, closeOthers);
  }
}

// Detecta se o módulo usa tema escuro (fundo escuro) lendo a luminância do
// background computado de body/html. Os módulos clínicos novos (8–12) são
// dark-themed; os Mods 1–6 são claros. A estética de tabela injetada precisa
// respeitar isso — senão dá texto escuro em fundo escuro (regra anti-contraste).
function isDarkModuleTheme(doc: Document): boolean {
  try {
    const view = doc.defaultView;
    if (!view) return false;
    for (const probe of [doc.body, doc.documentElement]) {
      if (!probe) continue;
      const bg = view.getComputedStyle(probe).backgroundColor || "";
      const m = bg.match(/rgba?\(([^)]+)\)/);
      if (!m) continue;
      const parts = m[1].split(",").map((s) => parseFloat(s));
      const [r, g, b, a = 1] = parts;
      if (a === 0) continue; // transparente: tenta o próximo nível
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return lum < 0.5;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function injectMobileResponsiveStyles(doc: Document): void {
  if (doc.getElementById(MOBILE_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = MOBILE_STYLE_ID;
  // Paleta de tabela ciente do tema do módulo (claro vs escuro).
  const dark = isDarkModuleTheme(doc);
  const T = dark
    ? {
        bg: "transparent",
        border: "rgba(255,255,255,0.12)",
        text: "var(--text-primary, #e8f4f8)",
        ruleTop: "rgba(255,255,255,0.10)",
        ruleRight: "rgba(255,255,255,0.07)",
        even: "rgba(255,255,255,0.045)",
        hover: "rgba(18,190,209,0.14)",
        firstCol: "var(--text-primary, #e8f4f8)",
        firstColStrong: "#a5f3fc",
        campoTexto: "var(--text-primary, #e8f4f8)",
        campoFundo: "var(--surface, rgba(255,255,255,0.07))",
        campoBorda: "var(--border, rgba(255,255,255,0.18))"
      }
    : {
        bg: "var(--surface, #ffffff)",
        border: "var(--border, #d8e2ea)",
        text: "var(--text, #0d2438)",
        ruleTop: "var(--rule, #eef2f7)",
        ruleRight: "var(--rule, #f1f5f9)",
        even: "#f8fafc",
        hover: "#eff5fa",
        firstCol: "#0d2438",
        firstColStrong: "#123c69",
        campoTexto: "var(--text, #0d2438)",
        campoFundo: "var(--surface, #ffffff)",
        campoBorda: "var(--border, #d8e2ea)"
      };
  style.textContent = `
    /* Garantias globais de fluidez dentro do iframe do modulo */
    *, *::before, *::after { box-sizing: border-box; }
    html, body { max-width: 100%; overflow-x: hidden; }
    img, svg, video, canvas, picture, iframe { max-width: 100%; height: auto; }
    figure { margin: 16px 0; max-width: 100%; }
    /* A caixa de diagrama ja rola por dentro (overflow-x: auto), mas ela mesma ficava
       mais larga que o pai — 350px num espaco de 241px no Mod. 1 — e ai quem cortava era
       o <details> acima, que esconde o excesso: o "Plano D — via aerea cirurgica" do
       algoritmo A/B/C/D sumia na borda. Limitando a caixa, a rolagem dela volta a ser o
       caminho para ver o diagrama inteiro, em vez de encolher o desenho e o texto dentro. */
    .svg-box { max-width: 100%; }
    .codex-svg-scroll { display: block; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    figure svg, figure img { display: block; margin: 0 auto; }
    pre, code, kbd, samp { white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }
    main, section, article { max-width: 100%; }
    h1, h2, h3, h4, h5, h6, p, li, td, th { overflow-wrap: break-word; word-break: normal; }
    table { max-width: 100%; }

    /* Quem pede menos movimento recebe menos movimento. Medido nos 17 modulos: 4.423
       elementos com transicao e 88 com animacao, contra UMA regra de
       prefers-reduced-motion em todos eles somados. Nao se desliga o movimento por
       completo (0s quebra callback de transitionend); encurta-se a quase zero. */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }

    /* PISO de todo campo de formulario do modulo — nao so os de .calc-grid.
       Perseguir nome de classe deixou de fora o CPES do Mod. 12, a calculadora do
       Mod. 13 e outros: medido, 8 controles ainda saiam como caixa BRANCA de Arial
       preta sobre fundo azul-escuro. Seletor de ELEMENTO, especificidade minima —
       qualquer regra do proprio modulo continua vencendo; isto so cobre quem nao tem
       regra nenhuma. As cores acompanham o tema do modulo. */
    input, select, textarea {
      font-family: inherit;
      color: ${T.campoTexto};
      background: ${T.campoFundo};
      border: 1px solid ${T.campoBorda};
      border-radius: 6px;
      padding: 5px 8px;
    }
    input:focus, select:focus, textarea:focus {
      outline: 1px solid var(--cyan, #12bed1); outline-offset: 1px;
    }

    /* No iOS, campo com fonte MENOR que 16px faz o Safari dar zoom sozinho ao receber
       foco — e nao volta: e o "zoom que come a tela". 16px e o limiar exato da Apple.
       Vem com !important de proposito: as regras dos modulos vao de 11 a 14px e
       venceriam. Decisao do autor, ciente de que os campos ficam maiores. */
    input, select, textarea { font-size: 16px !important; }

    /* Campos das calculadoras dos modulos. Os Mod. 3 e 5 trazem a marcacao .calc-grid mas
       nunca ganharam o CSS dos campos: medido, 4 de 4 e 5 de 5 saiam como caixa BRANCA de
       Arial preta sobre o fundo azul-escuro do modulo. Sao a calculadora de volume
       corrente por peso predito e a de dose -> mL/h — as duas em uso.
       As cores vem das variaveis do modulo quando existirem. */
    .calc-grid input, .calc-grid select, .calc-grid textarea {
      width: 100%; box-sizing: border-box;
      font-family: inherit; font-size: 14px; line-height: 1.3;
      padding: 7px 9px; border-radius: 6px;
      background: var(--surface, rgba(255,255,255,.07));
      color: var(--text-primary, #e8f4f8);
      border: 1px solid var(--border, rgba(255,255,255,.18));
    }
    .calc-grid input:focus, .calc-grid select:focus, .calc-grid textarea:focus {
      outline: 1px solid var(--cyan, #12bed1); outline-offset: 1px;
    }
    .calc-grid label { display: block; font-size: 12.5px; margin: 0 0 3px; opacity: .9; }
    .calc-grid > div { margin-bottom: 8px; }

    /* O resultado e o memorial de calculo em linhas separadas. Nos Mod. 3 e 5 os dois sao
       inline e saem colados: "Resultado: 420.00 mcg/h0.1 mcg/kg/min x 70 kg..." — o valor
       encosta na conta e da para ler 420.000. No modulo das Calculadoras ja e bloco. */
    .calc-result > strong { display: block; }
    .calc-result > span { display: block; margin-top: 3px; }

    /* Container scrollavel das tabelas (respeita o conteudo). */
    /* Corpo da referencia: um unico item de flex, que pode encolher. */
    .codex-ref-corpo { flex: 1 1 auto; min-width: 0; }

    .codex-table-scroll {
      display: block;
      max-width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      /* Mesma exposicao do corpo do modal: sem contencao, o gesto lateral que
         alcanca o fim da tabela encadeia para a pagina e arrasta a tela. */
      overscroll-behavior-x: contain;
      margin: 16px 0;
    }
    .codex-table-scroll table {
      table-layout: auto;
      width: 100%;
    }

    /* ===========================================================
       ESTETICA BASE: hierarquia visual e leitura facil.
       Aplicada em qualquer viewport; sobrescrita no modo cards.
       =========================================================== */
    table {
      background: ${T.bg};
      border: 1px solid ${T.border} !important;
      border-collapse: separate !important;
      border-spacing: 0;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(13, 36, 56, 0.05), 0 1px 2px rgba(13, 36, 56, 0.04);
      margin: 16px 0;
      overflow: hidden;
    }
    table thead th {
      background: linear-gradient(180deg, #1a4d80 0%, #123c69 100%) !important;
      color: #ffffff !important;
      font-size: 12.5px;
      font-weight: 700;
      letter-spacing: 0.05em;
      padding: 11px 14px;
      text-align: left;
      text-transform: uppercase;
      vertical-align: middle;
      border: 0 !important;
      border-right: 1px solid rgba(255, 255, 255, 0.14) !important;
    }
    table thead th:last-child { border-right: 0 !important; }
    table tbody td, table tbody th {
      color: ${T.text};
    }
    table tbody td {
      border: 0 !important;
      border-top: 1px solid ${T.ruleTop} !important;
      border-right: 1px solid ${T.ruleRight} !important;
      padding: 11px 14px;
      vertical-align: top;
      font-size: 14px;
      line-height: 1.5;
    }
    table tbody td:last-child { border-right: 0 !important; }
    table tbody tr:first-child td { border-top: 0 !important; }
    table tbody tr {
      transition: background-color 140ms ease;
    }
    table tbody tr:nth-child(even) td { background: ${T.even}; }
    table tbody tr:hover td { background: ${T.hover}; }
    /* Primeira coluna como "topico" da linha (entidade): mais peso e cor */
    table tbody td:first-child {
      color: ${T.firstCol};
      font-weight: 600;
    }
    table tbody td:first-child strong { color: ${T.firstColStrong}; font-weight: 700; }

    /* ===========================================================
       MOBILE <768px: tabela vira lista de cards rotulados.
       Primeira <td> da linha vira "titulo" do card.
       =========================================================== */
    @media (max-width: 767px) {
      body  {
        font-size: 14px !important;
        line-height: 1.65 !important;
        /* Mobile: maximiza largura util de leitura.
           - Lateral 12px (era 16): respiro suficiente em iPhone com tela curva,
             ganha 8px de texto efetivo.
           - Bottom: reserva pra pill flutuante (40px altura + 10px margem do
             edge + safe-area do iPhone + ~8px folga).
           Em conjunto com reducoes em main/section/.hero abaixo, a coluna
           de texto efetivo (<p>) sai de ~74% pra ~83% da viewport. */
        padding-left: 12px !important;
        padding-right: 12px !important;
        /* Padding-bottom removido do body interno — era incluído no body.scrollHeight
           e causava espaço em branco após os botões de navegação.
           O padding para a action bar mobile fica no .workspace externo (styles.css). */
        padding-bottom: 16px !important;
      }
      main  { padding: 10px !important; }

      /* O hero sangra ate a borda da tela, e a margem negativa tem que ser EXATAMENTE o
         padding do main da linha acima. Os modulos 1-6 trazem -26px (o valor do desktop)
         e passavam 4px da borda direita — que o overflow-x:hidden do body CORTA, em vez
         de rolar: o conteudo nao fica escondido atras de uma barra, fica inalcancavel.
         Os modulos 11-12 traziam -16px e paravam 6px ANTES da borda. Com os dois numeros
         no mesmo lugar, o sangramento e exato em todos. */
      .hero { margin-left: -10px !important; margin-right: -10px !important; }

      /* Medidor de escore (CPOT, BPS): a barra tem min-width:280px, que somada ao rotulo,
         ao gap e ao padding passa da largura do celular. O rotulo do limiar —
         "maior ou igual a 3, dor relevante" — terminava 35px FORA da tela, cortado.
         Empilhado, a barra recebe a largura toda e os dois rotulos cabem inteiros. */
      .score-gauge {
        display: flex !important; flex-direction: column !important;
        align-items: stretch !important; width: 100% !important;
        gap: 6px !important; padding: 8px 10px !important;
      }
      .score-gauge__bar { min-width: 0 !important; width: 100% !important; }

      /* Os rotulos dentro da barra sao proporcionais a faixa do escore, e no celular a
         faixa menor fica estreita demais para o texto: medido, "0-2 controlada" pede 91px
         numa banda de 88px e "3-4 ausente" pede 72px numa de 62px. Com nowrap + ellipsis
         (o que estava aqui) o rotulo era truncado. Deixar quebrar em duas linhas preserva
         a proporcao — que e a informacao da barra — e o texto inteiro. */
      .score-gauge__bar { height: auto !important; min-height: 22px !important;
        align-items: stretch !important; }
      .score-gauge__safe, .score-gauge__alert {
        white-space: normal !important; text-align: center;
        line-height: 1.2; padding: 3px 4px !important;
      }

      /* Hero e sections do modulo: padding lateral reduzido pra ganhar
         largura de leitura. Padding vertical mantido pra preservar respiro
         entre blocos. As fontes ja sao responsivas via clamp() abaixo. */
      .hero { padding: 18px 12px !important; border-radius: 14px !important; }
      .hero h2 { font-size: clamp(17px, 5vw, 23px) !important; line-height: 1.25 !important; }
      .hero p  { font-size: 14px !important; }
      section  { padding: 14px 8px !important; border-radius: 12px !important; }
      section h2 { font-size: clamp(17px, 4.5vw, 23px) !important; line-height: 1.3 !important; }
      section h3 { font-size: clamp(14px, 4vw, 17px) !important; }

      /* Grids do modulo empilham em coluna unica.

         Os grids de CARTAO nao usavam essas classes e ficavam de fora. Eles sao
         repeat(auto-fit, minmax(320px, 1fr)): com 275px de container, a trilha de 320px
         NAO encolhe — o cartao sai 45px alem do pai e 14px alem da tela, e o texto dele
         ("Mascara nao reinalante", a descricao inteira) e cortado pelo overflow-x:hidden
         do body. Nao rola: some.

         minmax(0, 1fr) tira o piso da trilha. Vale para todos de uma vez porque o
         defeito e o mesmo: piso fixo maior que o container. */
      .grid-2, .grid-3, .grid-4,
      .scenario-grid, .error-grid, .drug-grid, .pattern-grid, .step-grid,
      .receptor-grid, .classification-grid, .renal-row, .scale-row {
        grid-template-columns: minmax(0, 1fr) !important;
      }

      /* Lista que virou grid (as referencias numeradas) e o cabecalho do caderno tinham
         trilha fixa maior que o container. Em lista comum a regra e inerte. */
      ol, ul, .quiz-chapter__summary, .quiz-q__form {
        grid-template-columns: minmax(0, 1fr) !important;
      }
      /* grid-template-areas define o NUMERO de colunas por conta propria e vence a
         linha acima: o cabecalho de capitulo do Caderno continuava em duas colunas, com
         o titulo espremido em 72px e oito linhas de altura. Zerando as areas, os tres
         elementos ocupam a largura toda e o cabecalho cai de 291px para 125px. */
      .quiz-chapter__summary, .quiz-q__form { grid-template-areas: none !important; }

      /* O mesmo defeito do grid, na versao flex: item de flex tem min-width:auto, entao
         nao encolhe abaixo do proprio conteudo e sai da linha — a revista de uma
         referencia terminava 153px fora da tela, cortada. Deixar a linha QUEBRAR resolve
         sem espremer: o item vai inteiro para a linha de baixo.

         Aqui nao serve min-width: 0 generico: ele conserta o corte mas AMASSA o que e
         inline — medido, o selo "ASCO/IDSA" do Mod. 9 caiu para 2,8px de largura. Em
         elemento que nao e flex, flex-wrap nao faz nada. */
      * { flex-wrap: wrap; }

      /* Campo de formulario largo (o seletor de diluicao do Mod. 5 tem uma opcao de
         301px) nao pode empurrar a coluna. */
      input, select, textarea, button { max-width: 100%; }

      /* Quebrar dentro da palavra quando nao ha outro lugar onde quebrar.

         Isto nao e cosmetico: overflow-wrap ANYWHERE reduz a largura MINIMA do texto,
         enquanto break-word (o que estava aqui) nao reduz. E a largura minima que o
         flex e o grid usam para decidir se o item pode encolher — por isso, a 320px,
         um titulo de referencia de 275px continuava saindo de um pai de 222px, cortado
         pelo overflow-x:hidden do body.

         A alternativa seria min-width: 0 em tudo, que conserta o corte mas AMASSA o
         que e inline: medido, o selo "ASCO/IDSA" do Mod. 9 caiu para 2,8px. Quebrar
         so acontece quando a palavra nao cabe de jeito nenhum. */
      * { overflow-wrap: anywhere; }
      /* Contrapeso do anywhere: ele reduz a largura minima do texto a UM caractere, e
         num flex o bloco de texto passa a caber em qualquer sobra em vez de descer para
         a linha de baixo. Medido: o texto do resultado do HEART coube em 36px e imprimiu
         uma palavra por linha; o do NIHSS, em 43px e 26 linhas. Com um minimo legivel, a
         linha quebra e o bloco desce inteiro. */
      .score-res-text, .nihss-result > div { min-width: 14ch; }
      /* O seletor universal tem especificidade ZERO, entao a regra global de cima
         (h1..h6, p, li, td, th com overflow-wrap: break-word) vencia justamente nos
         elementos que mais carregam texto longo. Medido no Mod. 12: o li de uma
         referencia ficava com break-word, o titulo
         "2026 AHA/ACC/ACCP/ACEP/CHEST/SCAI/SHM/SIR/SVM..." nao quebrava, e o li
         transbordava 64px (scrollWidth 341 contra clientWidth 277) — cortado na borda
         do cartao. Repetir a lista aqui empata a especificidade e, por vir depois,
         vence no celular. */
      h1, h2, h3, h4, h5, h6, p, li, td, th, dt, dd, figcaption, span, em, strong, a {
        overflow-wrap: anywhere;
      }

      table {
        display: block !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        overflow: visible !important;
      }
      table thead { display: none !important; }
      table tbody, table tr, table td { display: block !important; width: 100% !important; }
      table tr {
        background: rgba(18,190,209,.06) !important;
        border: 1px solid rgba(18,190,209,.2) !important;
        border-left: 4px solid rgba(18,190,209,.5) !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 8px rgba(0,0,0,.2) !important;
        margin: 0 0 14px 0 !important;
        padding: 14px 16px !important;
      }
      table tr:hover td { background: transparent !important; }
      table tbody tr:nth-child(even) td { background: transparent !important; }

      table td {
        border: 0 !important;
        padding: 6px 0 !important;
        font-size: 14px !important;
        line-height: 1.5 !important;
        background: transparent !important;
        color: rgba(232,244,248,.88) !important;
      }
      /* Primeira td: TITULO do card */
      table td:first-child {
        border-bottom: 1px solid rgba(18,190,209,.15) !important;
        color: #a5f3fc !important;
        font-size: 14px !important;
        font-weight: 700 !important;
        margin-bottom: 8px !important;
        padding: 0 0 10px 0 !important;
      }
      table td:first-child::before { content: none !important; }
      table td:first-child strong { color: #a5f3fc; font-size: 14px; }

      /* Demais td: separadas por hairline, com rotulo da coluna */
      table td + td { border-top: 1px solid rgba(18,190,209,.1) !important; padding-top: 8px !important; margin-top: 4px !important; }
      table td[data-label]:not(:first-child)::before {
        color: rgba(207,238,243,.65);
        content: attr(data-label);
        display: block;
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.06em;
        margin-bottom: 3px;
        text-transform: uppercase;
      }

      /* SVG diagramas em largura total. */
      .svg-box, .svg-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .svg-box svg { min-width: 320px; }
    }

    /* ===========================================================
       TABLET 768-1023px:
       - Tabelas LARGAS (>=5 colunas) viram cards (mesma logica do mobile).
       - Tabelas NORMAIS (<=4 colunas) ganham layout compacto, respeita
         o container — sem min-width, sem scroll horizontal.
       =========================================================== */
    @media (min-width: 768px) and (max-width: 1023px) {
      /* Tabelas largas: cards rotulados com primeira td como titulo */
      table.codex-wide-table {
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        display: block;
      }
      table.codex-wide-table thead { display: none !important; }
      table.codex-wide-table tbody,
      table.codex-wide-table tr,
      table.codex-wide-table td { display: block !important; width: 100%; }
      table.codex-wide-table tr {
        background: rgba(18,190,209,.06) !important;
        border: 1px solid rgba(18,190,209,.2) !important;
        border-left: 4px solid rgba(18,190,209,.5) !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 8px rgba(0,0,0,.2) !important;
        margin: 0 0 14px 0 !important;
        padding: 14px 18px !important;
      }
      table.codex-wide-table tr:hover td { background: transparent !important; }
      table.codex-wide-table tbody tr:nth-child(even) td { background: transparent !important; }
      table.codex-wide-table td {
        border: 0 !important;
        padding: 6px 0 !important;
        font-size: 14px !important;
        line-height: 1.5 !important;
        background: transparent !important;
      }
      table.codex-wide-table td:first-child {
        border-bottom: 1px solid #eef2f7 !important;
        color: #123c69 !important;
        font-size: 17px!important;
        font-weight: 700 !important;
        margin-bottom: 8px !important;
        padding: 0 0 10px 0 !important;
      }
      table.codex-wide-table td:first-child::before { content: none !important; }
      table.codex-wide-table td:first-child strong { color: #123c69; font-size: 17px; }
      table.codex-wide-table td + td { border-top: 1px solid #eef2f7 !important; padding-top: 8px !important; margin-top: 4px !important; }
      table.codex-wide-table td[data-label]:not(:first-child)::before {
        color: #5f6b7a;
        content: attr(data-label);
        display: block;
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.06em;
        margin-bottom: 3px;
        text-transform: uppercase;
      }

      /* Tabelas normais (compactas) */
      /* NOTA: overflow-x permanece auto em todos os viewports — evita que
         tabelas em .figure-split (imagem + tabela lado a lado) transbordem
         o container. Antes era "visible" no desktop, causando overflow. */
      .codex-table-scroll { overflow-x: auto; }
      table:not(.codex-wide-table) thead th {
        font-size: 10.5px;
        padding: 9px 10px;
      }
      table:not(.codex-wide-table) tbody td {
        padding: 8px 10px;
        font-size: 12.5px;
        line-height: 1.45;
        word-break: normal;
        overflow-wrap: anywhere;
      }
    }

    /* ===========================================================
       DESKTOP >=1024px: tabela completa, sem scroll forcado.
       =========================================================== */
    @media (min-width: 1024px) {
      .codex-table-scroll { overflow-x: visible; }
    }
  `;
  doc.head.appendChild(style);
}

function applyDataLabelsToTables(doc: Document): void {
  const tables = doc.querySelectorAll<HTMLTableElement>("table");
  tables.forEach((table) => {
    if ((table.dataset as DOMStringMap)[TABLE_LABELS_FLAG] === "true") return;

    // Coletar rotulos das colunas a partir do thead (ou do primeiro tr com th).
    const headerCells = table.querySelectorAll<HTMLTableCellElement>("thead th");
    let labels: string[] = [];
    if (headerCells.length > 0) {
      labels = Array.from(headerCells).map((th) => (th.textContent || "").trim());
    } else {
      const firstRow = table.querySelector("tr");
      if (firstRow) {
        const ths = firstRow.querySelectorAll("th");
        if (ths.length > 0) {
          labels = Array.from(ths).map((th) => (th.textContent || "").trim());
        }
      }
    }

    if (labels.length === 0) {
      (table.dataset as DOMStringMap)[TABLE_LABELS_FLAG] = "true";
      return;
    }

    // Marca como "wide" quando tem >=5 colunas: no tablet (768-1023) ela
    // vira cards rotulados em vez de tabela apertada.
    if (labels.length >= 5) {
      table.classList.add("codex-wide-table");
    }

    // Aplicar data-label em cada td do tbody.
    const bodyRows = table.querySelectorAll<HTMLTableRowElement>("tbody > tr");
    bodyRows.forEach((row) => {
      const cells = row.querySelectorAll<HTMLTableCellElement>("td");
      cells.forEach((cell, index) => {
        if (cell.getAttribute("data-label")) return;
        const label = labels[index] || "";
        if (label) cell.setAttribute("data-label", label);
      });
    });

    // Wrap em container scrollavel.
    if (!table.parentElement?.classList.contains("codex-table-scroll")) {
      const wrap = doc.createElement("div");
      wrap.className = "codex-table-scroll";
      table.parentElement?.insertBefore(wrap, table);
      wrap.appendChild(table);
    }

    (table.dataset as DOMStringMap)[TABLE_LABELS_FLAG] = "true";
  });
}

/** Um <li> em flex com texto solto vira VARIOS itens de flex: cada trecho de texto entre
    elementos conta como item proprio, e item de flex nao encolhe abaixo do conteudo. Numa
    lista de referencias isso manda a revista para uma linha sozinha e faz a seguinte
    comecar com ponto; antes das correcoes de largura, mandava o titulo para fora da tela.

    Envolver tudo que vem depois do numero num unico <span> devolve o texto ao fluxo
    continuo — que e como o Mod. 11 ja estava escrito. Feito aqui, e nao no HTML, porque
    sao 13 itens em quatro modulos e mexer em texto de referencia por script e onde o
    estrago passa despercebido: mover nos nao altera um caractere. */
/** Piso de 10,5px tambem DENTRO do diagrama.

    Um <text> de SVG nao mede o que parece: o rotulo tem um tamanho em unidades do
    viewBox, e o que chega na tela e esse numero multiplicado pela escala do desenho.
    Medido a 375px: o Mod. 3 desenha num viewBox de 1000 unidades dentro de 320px —
    escala 0,32 — entao um rotulo de 11 unidades vira 3,5px REAIS. Os 150 textos de SVG
    do manual estavam TODOS abaixo do piso, e `getComputedStyle` nao denunciava porque
    devolve as unidades, nao os pixels.

    Aumentar o rotulo colidiria com o traçado. Em vez disso o desenho recebe a largura
    MINIMA em que o menor rotulo alcanca 10,5px e a caixa rola — o mesmo tratamento que
    as tabelas largas ja tem. O desenho mantem as proporcoes; quem le, rola. */
/** O numero que a calculadora devolve muda sem aviso para quem usa leitor de tela:
    medido, 25 blocos de resultado no modulo das Calculadoras e NENHUM com `aria-live`.
    Quem digita o peso ouve o campo, nao o resultado — numa conta de dose isso e a
    informacao inteira. `polite` espera a pessoa parar de digitar; `atomic` faz ler o
    bloco todo (valor + unidade + memoria de calculo), nao so o pedaco que mudou. */
function anunciaResultados(doc: Document): void {
  const alvos = doc.querySelectorAll(
    ".calc-result, .score-result, .nihss-result, .med-result"
  );
  alvos.forEach((el) => {
    if (el.getAttribute("aria-live")) return;
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    if (!el.getAttribute("role")) el.setAttribute("role", "status");
  });
}

function garantePisoNosDiagramas(doc: Document): void {
  const PISO = 10.5;
  const vista = doc.defaultView;
  if (!vista) return;
  doc.querySelectorAll("svg").forEach((svg) => {
    const el = svg as SVGSVGElement & { dataset: DOMStringMap };
    if (el.dataset.codexPisoDiagrama) return;
    /* O tamanho REAL de um rotulo e o valor em unidades vezes a escala que a matriz de
       tela aplica — e um <g transform="scale(...)"> dentro do desenho muda essa escala
       sem mudar o font-size. Medir pela matriz, e nao pelo viewBox, e o que faz a conta
       fechar: escalar a largura do SVG por k escala TODO rotulo por k. */
    let menorReal = Infinity;
    svg.querySelectorAll("text, tspan").forEach((t) => {
      const g = t as SVGGraphicsElement;
      const caixa = g.getBoundingClientRect();
      if (caixa.width <= 0 || caixa.height <= 0) return;
      if (!(t.textContent || "").trim()) return;
      const unidades = parseFloat(vista.getComputedStyle(t).fontSize);
      if (!Number.isFinite(unidades) || unidades <= 0) return;
      let escala = 1;
      try {
        const m = g.getScreenCTM();
        if (m) escala = Math.abs(m.a);
      } catch {
        /* sem matriz: fica a escala 1 */
      }
      const real = unidades * escala;
      if (real > 0 && real < menorReal) menorReal = real;
    });
    const atual = svg.getBoundingClientRect().width;
    if (!Number.isFinite(menorReal) || menorReal >= PISO || atual <= 0) {
      el.dataset.codexPisoDiagrama = "ok";
      return;
    }
    /* 5% de folga: com 2% cinco rotulos do Mod. 1 paravam em 10,3px — o arredondamento
       de sub-pixel come a diferenca quando o desenho tem grupo com escala propria. */
    const precisa = Math.ceil(atual * (PISO / menorReal) * 1.05);
    /* garante um ancestral que role, sem criar outro se ja houver */
    let rolante: HTMLElement | null = null;
    let p = svg.parentElement;
    while (p && p !== doc.body) {
      const ov = vista.getComputedStyle(p).overflowX;
      if (ov === "auto" || ov === "scroll") { rolante = p; break; }
      p = p.parentElement;
    }
    if (!rolante) {
      const caixa = doc.createElement("div");
      caixa.className = "codex-svg-scroll";
      svg.parentElement?.insertBefore(caixa, svg);
      caixa.appendChild(svg);
    }
    (svg as unknown as HTMLElement).style.minWidth = precisa + "px";
    el.dataset.codexPisoDiagrama = String(precisa);
  });
}

function agrupaCorpoDaReferencia(doc: Document): void {
  const vista = doc.defaultView;
  if (!vista) return;
  const ehMarcador = (el: Element): boolean => {
    const c = typeof el.className === "string" ? el.className : "";
    return c.indexOf("ref-num") >= 0 || c.indexOf("ref-tag") >= 0;
  };
  doc.querySelectorAll("li").forEach((li) => {
    if (li.dataset.codexRefAgrupada) return;
    if (vista.getComputedStyle(li).display !== "flex") return;
    const nos = Array.from(li.childNodes);
    const temTextoSolto = nos.some(
      (n) => n.nodeType === 3 && (n.textContent ?? "").trim().length > 3
    );
    if (!temTextoSolto) return;
    const primeiro = nos.find((n) => n.nodeType === 1) as Element | undefined;
    const marcador = primeiro && ehMarcador(primeiro) ? primeiro : null;
    const inicio = marcador ? nos.indexOf(marcador) + 1 : 0;
    const corpo = doc.createElement("span");
    corpo.className = "codex-ref-corpo";
    for (let i = inicio; i < nos.length; i++) corpo.appendChild(nos[i]);
    li.appendChild(corpo);
    li.dataset.codexRefAgrupada = "true";
  });
}

function injectMobileTableInteraction(doc: Document): void {
  const viewport = doc.defaultView;
  if (!viewport || viewport.innerWidth >= 768) return;
  if (doc.getElementById(TABLE_MODAL_STYLE_ID)) return;

  // --- CSS do modal ---
  const style = doc.createElement("style");
  style.id = TABLE_MODAL_STYLE_ID;
  style.textContent = `
    #${TABLE_MODAL_ID} {
      background: rgba(4, 20, 36, 0.96);
      display: none;
      flex-direction: column;
      inset: 0;
      position: fixed;
      z-index: 9999;
    }
    #${TABLE_MODAL_ID}.codex-tmodal--open { display: flex; }
    .codex-tmodal__bar {
      align-items: center;
      background: #0d2438;
      border-bottom: 1px solid rgba(255,255,255,0.12);
      color: #fff;
      display: flex;
      flex-shrink: 0;
      gap: 12px;
      justify-content: space-between;
      padding: 14px 16px;
    }
    .codex-tmodal__title {
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .codex-tmodal__close {
      background: rgba(255,255,255,0.14);
      border: 1px solid rgba(255,255,255,0.22);
      border-radius: 8px;
      color: #fff;
      cursor: pointer;
      flex-shrink: 0;
      font: inherit;
      font-size: 12.5px;
      padding: 8px 14px;
    }
    .codex-tmodal__body {
      -webkit-overflow-scrolling: touch;
      background: #fff;
      flex: 1;
      overflow: auto;
      /* Sem contencao o gesto que alcanca o fim da tabela ENCADEIA para o
         ancestral e arrasta a pagina de lado: o iframe e full-height e quem rola
         e a pagina de fora, entao o leitor perde a tela. A contencao barra o
         encadeamento SEM desabilitar o gesto — touch-action: none faria o
         oposto, e foi o erro do ERR-050. */
      overscroll-behavior: contain;
      padding: 16px;
    }
    /* O modal tem fundo branco e mostra a tabela COMPLETA (não-card). O bloco
       mobile (@media <768px) transforma toda <table> em cards e força texto claro
       com !important — o que, sobre o branco do modal, dá texto claro-em-branco
       (mesma classe do ERR-018). Aqui reverte-se a tabela do modal para tema CLARO
       real (texto escuro sobre branco), vencendo o bloco mobile por specificity
       (.codex-tmodal__body … ) + !important. */
    .codex-tmodal__body table {
      border-collapse: collapse !important;
      display: table !important;
      min-width: 480px;
      width: 100%;
      background: #fff !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }
    .codex-tmodal__body thead { display: table-header-group !important; }
    .codex-tmodal__body tbody { display: table-row-group !important; }
    .codex-tmodal__body tr {
      display: table-row !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    .codex-tmodal__body th,
    .codex-tmodal__body td {
      display: table-cell !important;
      width: auto !important;
      border: 1px solid #d8e0ea !important;
      color: #172033 !important;
      background: transparent !important;
      font-size: 14px !important;
      margin: 0 !important;
      padding: 10px 12px !important;
      text-align: left;
      vertical-align: top;
    }
    .codex-tmodal__body th { background: #123c69 !important; color: #fff !important; }
    .codex-tmodal__body td:first-child {
      color: #172033 !important;
      font-weight: 600 !important;
      margin-bottom: 0 !important;
    }
    .codex-tmodal__body td:first-child strong { color: #123c69 !important; }
    .codex-tmodal__body td::before,
    .codex-tmodal__body td:first-child::before,
    .codex-tmodal__body td[data-label]::before { content: none !important; }
    .codex-tmodal__body tbody tr:nth-child(even) td { background: #f8fafc !important; }
    .codex-table-expand-btn {
      background: #eef3f8;
      border: 1px solid #d8e0ea;
      border-radius: 10px;
      color: #123c69;
      cursor: pointer;
      display: block;
      font-family: inherit;
      font-size: 12.5px;
      font-weight: 600;
      margin: 4px 0 18px;
      padding: 10px 14px;
      text-align: center;
      width: 100%;
    }
    .codex-table-expand-btn:active { background: #dce9f6; }
  `;
  doc.head.appendChild(style);

  // --- Elemento modal ---
  const modal = doc.createElement("div");
  modal.id = TABLE_MODAL_ID;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Tabela expandida");

  const bar = doc.createElement("div");
  bar.className = "codex-tmodal__bar";

  const titleEl = doc.createElement("span");
  titleEl.className = "codex-tmodal__title";
  titleEl.textContent = "Tabela";

  const closeBtn = doc.createElement("button");
  closeBtn.className = "codex-tmodal__close";
  closeBtn.type = "button";
  closeBtn.textContent = "✕ Fechar";

  const bodyEl = doc.createElement("div");
  bodyEl.className = "codex-tmodal__body";

  bar.appendChild(titleEl);
  bar.appendChild(closeBtn);
  modal.appendChild(bar);
  modal.appendChild(bodyEl);
  doc.body.appendChild(modal);

  // Ancora o modal de tabela ao viewport REAL. Como os modais de calculadora, position:fixed
  // dentro do iframe de altura-de-conteúdo referencia a caixa do iframe (não a tela) → o modal
  // cobria o módulo inteiro e aparecia EM BRANCO quando o usuário estava rolado. Este código roda
  // no contexto do PAI (applyIframeSafetyLayer), então pego a área visível via frameElement.
  const positionTableModal = () => {
    const fe = doc.defaultView?.frameElement;
    if (!fe) return;
    const r = fe.getBoundingClientRect();
    const pWin = fe.ownerDocument.defaultView;
    // visualViewport reflete o que está REALMENTE visível: encolhe com o teclado virtual e
    // acompanha a rotação (girar para paisagem é o gesto natural para ler tabela larga).
    const vv = pWin?.visualViewport ?? null;
    const pH = vv?.height ?? pWin?.innerHeight ?? doc.defaultView?.innerHeight ?? 0;
    const vOff = vv?.offsetTop ?? 0;
    const vTop = Math.max(0, vOff - r.top);
    const vBottom = Math.min(fe.clientHeight || doc.documentElement.scrollHeight, vOff - r.top + pH);
    const vH = Math.max(200, vBottom - vTop);
    modal.style.position = "absolute";
    modal.style.top = `${Math.round(vTop)}px`;
    modal.style.height = `${Math.round(vH)}px`;
    modal.style.bottom = "auto";
  };
  // A área visível muda DEPOIS da abertura (rotação, teclado, barra de URL) e o scroll do pai
  // fica travado — sem re-medir, a parte de baixo do modal fica inalcançável (ERR-025).
  // Sem listener de scroll: foi o que inflou o scrollHeight no ERR-023.
  // Segue também o SCROLL do pai: o `top` do modal está em coords do documento e o scrollY muda
  // sozinho (reclamp na mudança de altura, barra de URL, rubber-band do iOS). Seguro porque
  // positionTableModal limita vBottom à caixa do iframe — o modal não estende o documento, que
  // era o mecanismo do ERR-023. Throttle de 1 reposição por frame: scroll dispara em rajada.
  let tableRepos: (() => void) | null = null;
  let tableReposQueued = false;
  const tableParentWin = () =>
    doc.defaultView?.frameElement?.ownerDocument.defaultView ?? doc.defaultView;
  const bindTableViewport = () => {
    unbindTableViewport();
    tableRepos = () => {
      if (tableReposQueued) return;
      tableReposQueued = true;
      requestAnimationFrame(() => {
        tableReposQueued = false;
        positionTableModal();
      });
    };
    const pWin = tableParentWin();
    pWin?.addEventListener("resize", tableRepos);
    pWin?.addEventListener("orientationchange", tableRepos);
    pWin?.addEventListener("scroll", tableRepos, { passive: true });
    pWin?.visualViewport?.addEventListener("resize", tableRepos);
    pWin?.visualViewport?.addEventListener("scroll", tableRepos);
  };
  function unbindTableViewport() {
    if (!tableRepos) return;
    const pWin = tableParentWin();
    pWin?.removeEventListener("resize", tableRepos);
    pWin?.removeEventListener("orientationchange", tableRepos);
    pWin?.removeEventListener("scroll", tableRepos);
    pWin?.visualViewport?.removeEventListener("resize", tableRepos);
    pWin?.visualViewport?.removeEventListener("scroll", tableRepos);
    tableRepos = null;
    tableReposQueued = false;
  }
  const lockTableScroll = (on: boolean) => {
    try {
      const fe = doc.defaultView?.frameElement;
      const pdoc = fe ? fe.ownerDocument : doc;
      if (pdoc?.documentElement) pdoc.documentElement.style.overflow = on ? "hidden" : "";
      if (pdoc?.body) pdoc.body.style.overflow = on ? "hidden" : "";
    } catch {
      /* noop */
    }
  };
  const closeModal = () => {
    modal.classList.remove("codex-tmodal--open");
    bodyEl.innerHTML = "";
    modal.style.position = "";
    modal.style.top = "";
    modal.style.height = "";
    modal.style.bottom = "";
    unbindTableViewport();
    lockTableScroll(false);
  };

  closeBtn.addEventListener("click", closeModal);
  doc.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape") closeModal();
  });

  // --- Botão de expandir por tabela ---
  const tables = doc.querySelectorAll<HTMLTableElement>("table");
  tables.forEach((table, idx) => {
    const section = table.closest("section");
    const heading = section?.querySelector("h3, h2");
    const tableTitle = heading?.textContent?.trim() ?? `Tabela ${idx + 1}`;

    const btn = doc.createElement("button");
    btn.className = "codex-table-expand-btn";
    btn.type = "button";
    btn.textContent = "↔ Ver tabela completa";
    btn.setAttribute("aria-label", `Expandir tabela: ${tableTitle}`);

    btn.addEventListener("click", () => {
      titleEl.textContent = tableTitle;
      bodyEl.innerHTML = table.outerHTML;
      modal.classList.add("codex-tmodal--open");
      positionTableModal();
      lockTableScroll(true);
      bindTableViewport();
      bodyEl.scrollTop = 0;
    });

    const anchor = table.closest(".codex-table-scroll") ?? table;
    anchor.parentElement?.insertBefore(btn, anchor.nextSibling);
  });
}

/**
 * Compactacao tipografica para modulos clinicos densos (Mod 1-6).
 *
 * Reduz cerca de 15% do scroll total por meio de:
 * - body font-size 16->15px, line-height 1.55->1.50
 * - main padding 34->22px (desktop), max-width restrito ja existente
 * - hero padding 34->22px, margin-bottom 28->18px, h2 34->28px
 * - section padding 26->18px (desktop), margin-bottom 22->14px
 * - section h2 25->21px (margens menores), h3 19->17px (margens menores)
 * - paragrafos, listas e bordas inter-blocos compactados
 *
 * NAO toca em:
 * - Tabelas (tem layout proprio em injectMobileResponsiveStyles)
 * - Inputs/labels/cards de calculadora (`.calc-*`, `.med-*`, `.dva-*`)
 * - Mobile (<=767px) — ja e compacto via outras regras
 *
 * Injetado DEPOIS dos estilos do modulo, ganha por ordem de cascata.
 */
function injectReadabilityCompactionStyles(doc: Document): void {
  if (doc.getElementById(COMPACT_READING_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = COMPACT_READING_STYLE_ID;
  style.textContent = `
    /* Aplicado apenas em viewports >=768px. Mobile mantem o que ja existe. */
    @media (min-width: 768px) {
      body {
        font-size: 14px;
        line-height: 1.5;
      }

      /* Seletor com .app pra ganhar specificity 0,0,1,1.
         max-width: 1200px — usa bem a largura em desktop (antes era 900px,
         deixando ~230px vazios em cada lado num viewport de 1366px).
         margin: 0 auto — centraliza o conteúdo.
         padding: clamp() — respiro proporcional ao viewport. */
      .app main {
        padding: 22px clamp(26px, 3vw, 60px);
        max-width: 1200px !important;
        margin: 0 auto;
      }

      .hero {
        padding: 22px 26px;
        margin-bottom: 18px;
        border-radius: 18px;
      }
      .hero h2 {
        font-size: 23px;
        line-height: 1.2;
        margin-bottom: 8px;
      }
      .hero p {
        font-size: 14px;
        line-height: 1.5;
      }
      .badge-row { margin-top: 12px; gap: 8px; }
      .badge { font-size: 10.5px; padding: 5px 9px; }
      .toc-note { font-size: 10.5px; margin-top: 8px; }

      section {
        padding: 18px 22px;
        margin-bottom: 14px;
        border-radius: 14px;
      }
      section h2 {
        font-size: 23px;
        line-height: 1.25;
        margin: 0 0 10px;
      }
      section h3 {
        font-size: 17px;
        line-height: 1.3;
        margin: 16px 0 8px;
      }
      section h4 {
        font-size: 14px;
        margin: 12px 0 6px;
      }

      /* Quando a section esta colapsada via .codex-section-collapsed, o h2 vira
         botao e ja tem padding proprio — nao mexer. */
      section.codex-section-collapsed {
        padding-bottom: 10px;
      }

      p {
        margin: 0 0 10px;
      }
      ul, ol {
        margin: 0 0 10px;
        padding-left: 22px;
      }
      li {
        margin-bottom: 4px;
      }
      ul ul, ol ol, ul ol, ol ul { margin: 4px 0 4px; }

      /* Cards e metricas no corpo do texto: compactar padding. */
      .card { padding: 12px 14px; }
      .card h4 { margin: 0 0 6px; }
      .metric { padding: 10px 12px; }
      .metric strong { font-size: 17px; }

      /* Alertas: compactar mas manter destaque. */
      .alert {
        padding: 10px 14px;
        margin: 10px 0;
        border-left-width: 4px;
      }

      /* Timeline (passo a passo) compactada. */
      .timeline { padding-left: 14px; }
      .timeline .step { margin: 0 0 12px; }

      /* Figure-card (radiologia/diagramas) compactada. */
      .figure-card { padding: 10px; margin: 10px 0; }
      .figure-card figcaption { font-size: 12.5px; margin-top: 6px; }

      /* Pager entre modulos: ja tem estilo proprio do iframeSafety, so reduzir margens. */
      .codex-pager { margin: 24px 0 16px; }
      .codex-pager__btn { padding: 14px 18px; }

      /* Footer de assinatura no fim do modulo. */
      .footer-sign { margin-top: 22px; }
    }
  `;
  doc.head.appendChild(style);
}

/* ===========================================================================
   MELHORIAS DE LEITURA (cross-cutting, todos os módulos) — runtime, sem editar
   o HTML clínico (hashes preservados):
   - #84: boxes informativos longos (.alert-info) viram recolhíveis ABERTOS.
          Alertas de perigo/aviso/sucesso NUNCA são tocados (segurança).
   - #85a/b: glossário — explica sigla/termo (inglês) na 1ª aparição por módulo,
          entre parênteses, sem traduzir nomes consagrados.
   - #85c: remove os colchetes [n] dos marcadores de lista de referência (.ref-num).
   =========================================================================== */

const READER_STYLE_ID = "codex-reader-enhancements-style";

// Glossário curado. Cada termo recebe UMA glosa na 1ª aparição do módulo.
// Regex case-sensitive com limite de palavra; nomes próprios/consagrados não são
// traduzidos, apenas explicados. Termos ausentes em um módulo simplesmente não
// geram glosa.
const GLOSSARY: ReadonlyArray<{ re: RegExp; gloss: string }> = [
  { re: /\bqSOFA\b/, gloss: "quick SOFA — triagem rápida de risco à beira-leito" },
  { re: /\bSOFA\b/, gloss: "Sequential Organ Failure Assessment — escore de disfunção orgânica" },
  { re: /\bSIRS\b/, gloss: "síndrome da resposta inflamatória sistêmica" },
  { re: /\bESBL\b/, gloss: "betalactamase de espectro estendido" },
  { re: /\bMRSA\b/, gloss: "Staphylococcus aureus resistente à meticilina" },
  { re: /\bVRE\b/, gloss: "enterococo resistente à vancomicina" },
  { re: /\bCLABSI\b/, gloss: "infecção de corrente sanguínea relacionada a cateter" },
  { re: /\bDTP\b/, gloss: "tempo diferencial de positividade das hemoculturas" },
  { re: /\bstewardship\b/, gloss: "uso racional/gerenciamento de antimicrobianos" },
  { re: /\brule-in\b/, gloss: "confirmar o diagnóstico" },
  { re: /\brule-out\b/, gloss: "descartar o diagnóstico" },
  { re: /\bMASCC\b/, gloss: "escore de risco da neutropenia febril; ≥ 21 = baixo risco" },
  { re: /\btwo-bag\b/, gloss: "sistema de duas bolsas, com e sem glicose" },
  { re: /\bmismatch\b/, gloss: "discordância clínico-radiológica — área cerebral ainda salvável" },
  { re: /\bLVO\b/, gloss: "oclusão de grande vaso" },
  { re: /\bDCI\b/, gloss: "isquemia cerebral tardia" },
  { re: /\bDAPT\b/, gloss: "dupla antiagregação plaquetária" },
  { re: /\bsPESI\b/, gloss: "índice simplificado de gravidade do TEP" },
  { re: /\bPESI\b/, gloss: "índice de gravidade do TEP" },
  { re: /\bDOAC\b/, gloss: "anticoagulante oral direto" },
  { re: /\bRSI\b/, gloss: "intubação em sequência rápida" },
  { re: /\bPOCUS\b/, gloss: "ultrassonografia à beira-leito" },
  { re: /\bPEEP\b/, gloss: "pressão positiva no final da expiração" },
  { re: /\bARDS\b/, gloss: "síndrome do desconforto respiratório agudo" },
  { re: /\bNIHSS\b/, gloss: "escala de AVC do NIH" },
  { re: /\bBHB\b/, gloss: "beta-hidroxibutirato" },
  { re: /\bAMR\b/, gloss: "resistência antimicrobiana" },
  { re: /\bTDM\b/, gloss: "monitorização terapêutica do fármaco" },
  { re: /\bAUC\b/, gloss: "área sob a curva concentração-tempo" },
  { re: /\bCRRT\b/, gloss: "terapia de substituição renal contínua" }
];

function injectReaderEnhancementStyles(doc: Document): void {
  if (doc.getElementById(READER_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = READER_STYLE_ID;
  style.textContent = `
    .codex-gloss { opacity: .72; font-style: italic; font-weight: 400;
    /* Sem font-size relativo: .94em de um pai já no piso (10,5px) resultava em 9,87px,
       abaixo do mínimo. A glosa herda o tamanho do texto que explica. */ }
    .codex-alert-collapsible > .codex-alert-head {
      align-items: baseline;
      cursor: pointer;
      display: flex;
      gap: 8px;
      user-select: none;
    }
    .codex-alert-chevron {
      display: inline-flex;
      flex-shrink: 0;
      transition: transform .2s ease;
      transform: translateY(2px);
    }
    .codex-alert-collapsed .codex-alert-chevron { transform: translateY(2px) rotate(-90deg); }
    .codex-alert-peek { display: none; opacity: .85; }
    .codex-alert-collapsible.codex-alert-collapsed .codex-alert-peek { display: inline; }
    .codex-alert-collapsible.codex-alert-collapsed .codex-alert-body { display: none; }
  `;
  doc.head.appendChild(style);
}

// #85c — remove os colchetes dos marcadores de referência: [1] -> 1.
function stripRefNumBrackets(doc: Document): void {
  doc.querySelectorAll<HTMLElement>("span.ref-num").forEach((el) => {
    if (el.dataset.codexDebracket === "true") return;
    el.dataset.codexDebracket = "true";
    const m = (el.textContent || "").trim().match(/^\[(\d+)\]$/);
    if (m) el.textContent = `${m[1]}.`;
  });
}

// #84 — boxes informativos longos viram recolhíveis (abertos). Robusto a qualquer
// estrutura interna (.alert-info costuma ser [span.alert-icon][conteúdo] sem um
// <strong> de título separável). Move o ícone + um chevron para um cabeçalho
// clicável e mostra um "peek" (teaser) só quando recolhido — sem duplicar o
// conteúdo no estado aberto. Perigo/aviso/sucesso nunca tocados (segurança).
function makeLongInfoAlertsCollapsible(doc: Document): void {
  const alerts = doc.querySelectorAll<HTMLElement>(".alert.alert-info");
  alerts.forEach((alert) => {
    if (alert.dataset.codexAlertCollapse) return;
    const text = (alert.textContent || "").trim();
    if (text.length < 220) {
      alert.dataset.codexAlertCollapse = "skip";
      return;
    }
    alert.dataset.codexAlertCollapse = "done";
    alert.classList.add("codex-alert-collapsible");

    const icon = alert.querySelector<HTMLElement>(":scope > .alert-icon");

    // Corpo recolhível = todo o conteúdo, exceto o ícone (que vai p/ o cabeçalho).
    const body = doc.createElement("div");
    body.className = "codex-alert-body";
    let node: ChildNode | null = alert.firstChild;
    while (node) {
      const next: ChildNode | null = node.nextSibling;
      if (node !== icon) body.appendChild(node);
      node = next;
    }

    const head = doc.createElement("div");
    head.className = "codex-alert-head";
    head.setAttribute("role", "button");
    head.setAttribute("tabindex", "0");
    head.setAttribute("aria-expanded", "true");
    if (icon) head.appendChild(icon);

    const chevron = doc.createElement("span");
    chevron.className = "codex-alert-chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.innerHTML =
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
    head.appendChild(chevron);

    const peekText = (body.textContent || "").trim();
    const peek = doc.createElement("span");
    peek.className = "codex-alert-peek";
    peek.textContent = peekText.slice(0, 72) + (peekText.length > 72 ? "…" : "");
    head.appendChild(peek);

    alert.appendChild(head);
    alert.appendChild(body);

    const toggle = () => {
      const collapsed = alert.classList.toggle("codex-alert-collapsed");
      head.setAttribute("aria-expanded", String(!collapsed));
    };
    head.addEventListener("click", toggle);
    head.addEventListener("keydown", (event) => {
      const ke = event as KeyboardEvent;
      if (ke.key === "Enter" || ke.key === " ") {
        ke.preventDefault();
        toggle();
      }
    });
  });
}

// #85a/b — glossário: explica a 1ª aparição de cada termo no módulo, entre
// parênteses, com uma glosa discreta. Opera só sobre nós de TEXTO (nunca toca
// HTML/atributos), pulando script/style/inputs/summary e glosas já inseridas.
function injectGlossary(doc: Document): void {
  const main = (doc.querySelector("main") as HTMLElement | null) || doc.body;
  if (!main) return;
  const dataset = main.dataset as DOMStringMap;
  if (dataset.codexGlossed === "true") return;
  dataset.codexGlossed = "true";

  const view = doc.defaultView;
  const NF = view ? view.NodeFilter : (globalThis as unknown as { NodeFilter: typeof NodeFilter }).NodeFilter;

  for (const entry of GLOSSARY) {
    const walker = doc.createTreeWalker(main, NF.SHOW_TEXT, {
      acceptNode(node: Node): number {
        const parent = (node as Text).parentElement;
        if (!parent) return NF.FILTER_REJECT;
        if (
          parent.closest(
            "script,style,input,textarea,select,option,summary,.codex-gloss,.codex-alert-chevron,h1,thead"
          )
        ) {
          return NF.FILTER_REJECT;
        }
        return entry.re.test(node.nodeValue || "") ? NF.FILTER_ACCEPT : NF.FILTER_SKIP;
      }
    });

    const node = walker.nextNode() as Text | null;
    if (!node || !node.parentNode) continue;
    const value = node.nodeValue || "";
    const m = value.match(entry.re);
    if (!m || m.index === undefined) continue;
    const end = m.index + m[0].length;
    const after = value.slice(end);
    // Já explicado logo a seguir? (abre parênteses) — não duplicar.
    if (/^\s*\(/.test(after)) continue;

    node.nodeValue = value.slice(0, end);
    const span = doc.createElement("span");
    span.className = "codex-gloss";
    span.textContent = ` (${entry.gloss})`;
    node.parentNode.insertBefore(span, node.nextSibling);
    if (after) node.parentNode.insertBefore(doc.createTextNode(after), span.nextSibling);
  }
}

/**
 * Intercepta cliques em links internos (`<a href="#xxx">`) dentro do iframe e os
 * converte em scroll direto para o elemento alvo no MESMO documento, abrindo
 * automaticamente sections colapsadas (modo acordeao).
 *
 * Sem essa intercepcao, o iframe (com `srcDoc`) tenta navegar para
 * `about:srcdoc#xxx`, o que em alguns navegadores recarrega o srcDoc inteiro e
 * dispara `onLoad` de novo, causando o efeito de "modulo sobreposto" relatado
 * pelo usuario. Tambem evita borbulhar para a janela top e causar troca de rota
 * no shell React.
 */
function interceptInternalLinks(doc: Document): void {
  const flag = "codexInternalLinksWired";
  const bodyDataset = doc.body.dataset as DOMStringMap;
  if (bodyDataset[flag] === "true") return;
  bodyDataset[flag] = "true";

  doc.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.charAt(0) !== "#" || href.length < 2) return;

      // Link com target=_blank/_top/_parent: deixar o browser cuidar (provavelmente
      // intencional para abrir em nova aba).
      const linkTarget = anchor.getAttribute("target");
      if (linkTarget === "_blank") return;

      const id = decodeURIComponent(href.slice(1));
      const dest = doc.getElementById(id);
      if (!dest) return;

      event.preventDefault();
      event.stopPropagation();

      // Auto-abre section colapsada (e fecha as irmas se estiver em acordeao).
      if (dest.classList.contains(COLLAPSE_COLLAPSED_CLASS)) {
        const h2 = dest.querySelector<HTMLElement>(`:scope > h2.${COLLAPSE_TOGGLE_CLASS}`);
        if (h2) h2.click();
      } else {
        // Pode ser um subelemento (h3, p, li com id) dentro de uma section colapsada.
        const parentSection = dest.closest<HTMLElement>(`section.${COLLAPSE_COLLAPSED_CLASS}`);
        if (parentSection) {
          const h2 = parentSection.querySelector<HTMLElement>(`:scope > h2.${COLLAPSE_TOGGLE_CLASS}`);
          if (h2) h2.click();
        }
        // Pode estar dentro de um <details> nativo recolhido.
        const parentDetails = dest.closest<HTMLDetailsElement>("details");
        if (parentDetails && !parentDetails.open) {
          parentDetails.open = true;
        }
      }

      dest.scrollIntoView({ block: "start", behavior: "smooth" });
    },
    true
  );
}

/** Injeta o reporter de altura no iframe para eliminar o scroll duplo.
 *  O iframe reporta sua scrollHeight ao parent via postMessage.
 *  O ModuleViewer escuta e ajusta dinamicamente a height do elemento iframe.
 */
function injectHeightReporter(doc: Document): void {
  const REPORTER_ID = "codex-height-reporter";
  if (doc.getElementById(REPORTER_ID)) return;
  const script = doc.createElement("script");
  script.id = REPORTER_ID;
  script.textContent = `
(function() {
  var lastH = 0;
  var rafId = null;

  function reportH() {
    var h = Math.max(
      document.body ? document.body.scrollHeight : 0,
      document.documentElement ? document.documentElement.scrollHeight : 0
    );
    // Só envia postMessage se a altura mudou mais de 10px — evita floods
    if (h > 0 && Math.abs(h - lastH) > 10) {
      lastH = h;
      window.parent.postMessage({ type: 'codex-iframe-height', height: h }, '*');
    }
  }

  // Throttle via requestAnimationFrame — máximo 1 verificação por frame
  // Evita flood de updates durante scroll, hover states e animações CSS
  function scheduleReport() {
    if (rafId) return;
    rafId = requestAnimationFrame(function() {
      rafId = null;
      reportH();
    });
  }

  // Relatórios iniciais + pontos tardios (1200/2500ms) e o evento 'load' garantem que a
  // altura FINAL seja reportada mesmo quando conteúdo tardio (imagens, fontes, seções)
  // muda o tamanho — evita o módulo "não exibir totalmente" (altura sub-reportada corta
  // o conteúdo, já que o iframe é dimensionado à altura reportada e não tem scroll próprio).
  setTimeout(reportH, 100);
  setTimeout(reportH, 500);
  setTimeout(reportH, 1200);
  setTimeout(reportH, 2500);
  window.addEventListener('load', function() { reportH(); scheduleReport(); });

  // Sinaliza 'codex-fonts-ready' ao ModuleViewer, que mantém o módulo invisível
  // (opacity:0, pointer-events:none) até recebê-lo. Idealmente esperamos
  // document.fonts.ready para evitar o reflow do swap fallback → DM Sans. MAS em
  // mobile/conexão lenta as fontes externas (Google Fonts) podem levar VÁRIOS
  // SEGUNDOS; sem teto de tempo, o módulo fica em branco e a rolagem não responde
  // esse tempo todo — percebido pelo leitor como "freeze" ao rolar durante o load.
  // Solução: corrida entre document.fonts.ready e um teto de ~900 ms — o que vier
  // primeiro revela o módulo. Se as fontes chegarem DEPOIS do teto, o swap ocorre
  // com o módulo já visível (FOUT breve + reajuste suave de altura via ResizeObserver),
  // muito preferível ao bloqueio de vários segundos.
  var fontsSignaled = false;
  function signalFontsReady() {
    if (fontsSignaled) return;
    fontsSignaled = true;
    reportH();
    window.parent.postMessage({ type: 'codex-fonts-ready' }, '*');
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function() {
      signalFontsReady();   // revela (se o teto ainda não revelou)
      scheduleReport();     // reporta a altura final após o swap de fonte
    });
  }
  // Teto de tempo: revela mesmo se as fontes ainda não resolveram (mobile lento)
  // ou se a Font Loading API não existir (browsers muito raros).
  setTimeout(signalFontsReady, 500);

  // Resize de viewport (rotação de tela mobile, etc.) — passive: não bloqueia scroll
  window.addEventListener('resize', scheduleReport, { passive: true });

  // ResizeObserver: dispara APENAS quando o body muda de tamanho (sections expandindo,
  // questões respondidas, etc.) — muito mais eficiente que MutationObserver com
  // attributes:true que disparava em todos os hover states e classes durante scroll
  if (window.ResizeObserver) {
    new ResizeObserver(scheduleReport).observe(document.body);
  } else {
    // Fallback para browsers antigos: MutationObserver mínimo (sem attributes)
    if (window.MutationObserver) {
      new MutationObserver(scheduleReport).observe(
        document.documentElement || document.body,
        { childList: true, subtree: true }
      );
    }
  }
})();
  `;
  (doc.head || doc.documentElement).appendChild(script);
}

// P3: modais de calculadora mais compactos no mobile — menos padding, título/notas menores,
// eyebrow redundante oculto (o modal já está rotulado). Reduz o scroll interno sem tocar em
// dose/fórmula/conteúdo clínico. Aplica ao Mod 14 (.codex-chip-open) e ao Mod 15 (.ov .modal).
function injectCompactModalStyles(doc: Document): void {
  const ID = "codex-compact-modal-styles";
  if (doc.getElementById(ID)) return;
  const style = doc.createElement("style");
  style.id = ID;
  style.textContent = `
/* Anti espaço-fantasma: min-height:100vh no root do módulo (ex.: .app do Mod 14) estica além
   do conteúdo DENTRO do iframe de altura-de-conteúdo (100vh = altura do iframe ≥ conteúdo real),
   deixando espaço vazio abaixo do fim da página. Neutraliza em qualquer módulo. */
html, body { min-height:0 !important; }
.app { min-height:0 !important; }
@media (max-width:767px){
  main > section.codex-chip-section.codex-chip-open{ padding:12px 11px 16px !important; }
  /* Modal de preenchimento mais curto: 2 colunas no mobile (lista de seleção mais densa). */
  main > section.codex-chip-open .calc-grid{ grid-template-columns:repeat(2,minmax(0,1fr)) !important; gap:8px !important; }
  main > section.codex-chip-open > .section-eyebrow{ display:none !important; }
  main > section.codex-chip-open > h2{ font-size:17px!important; line-height:1.2 !important; margin:0 34px 7px 0 !important; }
  main > section.codex-chip-open h3{ font-size:14px!important; margin:9px 0 5px !important; }
  main > section.codex-chip-open .calc-panel{ padding:9px 10px !important; margin:7px 0 !important; }
  /* Fontes menores no modal (pedido do usuário — caber melhor na tela), mas NUNCA abaixo
     do piso de 10,5px do design system. Estas regras viviam em 8,5–10px e escaparam da
     varredura de legibilidade porque ela percorreu os 17 HTMLs — e o CSS ofensor é
     INJETADO pelo pai, não mora no módulo. Só valem em ≤767px com o modal aberto, que é
     exatamente o estado padrão do modo LITE no celular. */
  main > section.codex-chip-open label{ font-size:10.5px !important; }
  main > section.codex-chip-open input, main > section.codex-chip-open select{ font-size:16px !important; padding:4px 5px !important; }
  main > section.codex-chip-open .calc-result, main > section.codex-chip-open .calc-result *{ font-size:10.5px !important; }
  main > section.codex-chip-open .strategy-card, main > section.codex-chip-open .strategy-card div{ font-size:10.5px !important; line-height:1.4 !important; }
  main > section.codex-chip-open .action{ font-size:12.5px!important; padding:8px 12px !important; }
  /* Módulo 15 (.ov .modal): fontes reduzidas — tabelas dos modais NÃO têm .block (seletor antigo
     não casava, por isso a pediatria seguia "grande"); mirar table/label/select/grp-title direto. */
  .ov .modal table td, .ov .modal table th{ font-size:10.5px !important; padding:.4rem .5rem !important; }
  .ov .modal td.drug{ font-size:10.5px !important; }
  .ov .modal td.note{ font-size:10.5px !important; }
  .ov .modal .modal-body label{ font-size:10.5px !important; }
  .ov .modal .modal-body select, .ov .modal .modal-body input{ font-size:16px !important; padding:.3rem .45rem !important; }
  .ov .modal .grp-title{ font-size:10.5px !important; margin:.55rem 0 .3rem !important; }
  .ov .modal .alert{ font-size:10.5px !important; padding:.45rem .6rem !important; }
  .ov .modal .modal-body p{ font-size:10.5px !important; }
  .ov .modal .modal-head h3{ font-size:14px!important; }
  /* Drogas em LINHA ÚNICA nos modais pediátricos (pedido: PCR tinha ~3 linhas por droga):
     nome à esquerda + dose à direita na MESMA linha; nota clínica vira sub-linha minúscula.
     !important obrigatório: o card-transform genérico de tabela (injectMobileResponsiveStyles)
     usa !important e era ele que empilhava cada droga em ~3 linhas. */
  .ov .modal table tr{ display:flex !important; flex-wrap:wrap !important; align-items:baseline; gap:0 8px; padding:.26rem 0 !important; border-bottom:1px solid rgba(255,255,255,.07) !important; margin:0 !important; }
  .ov .modal table td, .ov .modal table th{ display:block !important; width:auto !important; border:0 !important; padding:0 !important; background:transparent !important; margin:0 !important; }
  .ov .modal td.drug{ flex:1 1 auto !important; font-weight:600; }
  .ov .modal td.calc{ flex:0 1 auto !important; text-align:right !important; }
  .ov .modal td.calc .sub, .ov .modal td.calc .alt{ display:inline !important; margin-left:4px !important; font-size:10.5px !important; }
  .ov .modal td.note{ flex:0 0 100% !important; width:100% !important; font-size:10.5px !important; margin-top:1px !important; text-align:left !important; }
  .ov .modal table thead{ display:none !important; }
  .ov .modal table td::before, .ov .modal table td[data-label]::before{ content:none !important; }
  main > section.codex-chip-open .calc-grid{ gap:7px !important; }
  main > section.codex-chip-open .calc-result{ padding:6px 8px !important; }
  main > section.codex-chip-open .compact-note,
  main > section.codex-chip-open .compact-note *{ font-size:10.5px !important; line-height:1.34 !important; }
  /* Módulo 15 (.ov > .modal) */
  .ov .modal .modal-head{ padding:.6rem .8rem !important; }
  .ov .modal .modal-head h3{ font-size:17px!important; }
  .ov .modal .modal-body{ padding:.7rem .8rem !important; }
  .ov .modal .modal-body p{ font-size:12.5px!important; margin:.35rem 0 !important; }
}
  `;
  (doc.head || doc.documentElement).appendChild(style);
}

/* Barra o ENCADEAMENTO de rolagem em tudo que rola por dentro.

   O iframe do modulo e full-height — quem rola e a pagina de fora. Sem contencao,
   o gesto que alcanca o fim de um rolavel interno (tabela larga, diagrama, corpo
   do modal) encadeia para o ancestral e arrasta a pagina inteira de lado; o leitor
   perde a tela e nao sabe como voltar. Relatado nas calculadoras de escore E nas
   de infusao, porque a causa e a camada, nao o modulo.

   Por que uma passada de DOM e nao mais uma regra de classe: a camada cria 5
   containers rolaveis, mas os modulos declaram outros 21 por conta propria, com
   nomes que esta funcao nao conhece. Regra por classe cobre o conhecido; varrer o
   estilo COMPUTADO cobre o esquecido (L-63).

   `contain` barra o encadeamento e PRESERVA o gesto. `touch-action: none` faria o
   oposto — desabilitaria o arrasto — e foi exatamente o erro do ERR-050. */
function contemEncadeamentoDeRolagem(doc: Document): void {
  const janela = doc.defaultView;
  if (!janela) return;
  doc.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const cs = janela.getComputedStyle(el);
    const rolaX = cs.overflowX === "auto" || cs.overflowX === "scroll";
    const rolaY = cs.overflowY === "auto" || cs.overflowY === "scroll";
    if (!rolaX && !rolaY) return;
    /* Nao mexer na raiz: conter no documento impediria o proprio scroll da pagina. */
    if (el === doc.documentElement || el === doc.body) return;
    el.style.setProperty("overscroll-behavior", "contain");
  });
}

export function applyIframeSafetyLayer(document: Document, opts?: SafetyLayerOptions): void {
  injectGuardStyles(document);
  injectMobileResponsiveStyles(document);
  injectCompactModalStyles(document);
  removeInternalModuleMenu(document);
  DOSE_RATE_PAIRS.forEach((pair) => wirePair(document, pair));
  applyDataLabelsToTables(document);
  injectMobileTableInteraction(document);

  // Melhorias de leitura cross-cutting (todos os módulos): boxes informativos
  // longos recolhíveis, glossário na 1ª aparição e remoção dos colchetes [n].
  injectReaderEnhancementStyles(document);
  stripRefNumBrackets(document);
  agrupaCorpoDaReferencia(document);
  garantePisoNosDiagramas(document);
  anunciaResultados(document);
  makeLongInfoAlertsCollapsible(document);
  injectGlossary(document);

  // Scroll único: iframe reporta altura ao parent para que o React
  // redimensione o elemento <iframe> e o scroll fique só na página.
  contemEncadeamentoDeRolagem(document);
  injectHeightReporter(document);

  if (opts?.compactReading) {
    injectReadabilityCompactionStyles(document);
  }

  if (opts?.collapse) {
    injectSectionCollapse(document, opts.collapse);
  }

  // Intercepta cliques de links internos APOS o collapse estar configurado, para
  // que a logica de auto-abrir section recolhida funcione.
  interceptInternalLinks(document);

  if (opts?.pager) {
    injectPagerStyles(document);
    injectModulePager(document, opts.pager);
  }
}

/* ===========================================================================
   MODO LITE — apresentação enxuta da calculadora aberta.

   Segue o mock aprovado (artefato "Modo LITE — mock das calculadoras"):

     FICA   vazão e dose (a resposta que a pessoa foi buscar)
     FICA   faixa terapêutica
     FICA   preparo e concentração final — o mL/h só vale para AQUELA
            concentração; separar os dois transforma o número numa armadilha
     FICA   apresentação da ampola
     RECOLHE  cenário, cuidados e segurança — "recolhido, a um toque.
              NÃO removido do app"

   Nada é apagado: os blocos são MOVIDOS para um <details> no fim do resultado.
   O LITE não recalcula nem reescreve conteúdo clínico — só reordena o que o
   módulo já produziu.

   O resultado é reescrito por innerHTML a cada cálculo, então o reagrupamento
   precisa ser reaplicado — daí o MutationObserver.
   =========================================================================== */
const LITE_STYLE_ID = "codex-lite-presentation";

function injectLiteStyles(doc: Document): void {
  if (doc.getElementById(LITE_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = LITE_STYLE_ID;
  style.textContent = `
.codex-lite details.lite-extra{ grid-column:1/-1; margin:2px 0 0; border:1px solid rgba(148,163,184,.22); border-radius:10px; background:rgba(148,163,184,.06); }
.codex-lite details.lite-extra > summary{ cursor:pointer; list-style:none; padding:9px 12px; font-size:12.5px; font-weight:700; color:#9db5bd; display:flex; align-items:center; gap:7px; }
.codex-lite details.lite-extra > summary::-webkit-details-marker{ display:none; }
.codex-lite details.lite-extra > summary::after{ content:"+"; margin-left:auto; font-size:14px; font-weight:700; line-height:1; }
.codex-lite details.lite-extra[open] > summary::after{ content:"–"; }
.codex-lite details.lite-extra[open] > summary{ border-bottom:1px solid rgba(148,163,184,.18); }
.codex-lite details.lite-extra > .med-card,
.codex-lite details.lite-extra > .calc-result{ margin:8px 10px 10px; }
/* O eyebrow do módulo é redundante no LITE — a barra do LITE já rotula a tela. */
.codex-lite main > section.codex-chip-open > .section-eyebrow{ display:none !important; }
`;
  (doc.head || doc.documentElement).appendChild(style);
}

/** Onde cada bloco morava antes de ser recolhido — para devolvê-lo ao lugar
 *  certo ao sair do LITE, e não a um pai qualquer. */
const liteHome = new WeakMap<Element, { pai: Element; antes: Node | null }>();

/** Move cenário + cuidados para um <details> no fim de cada resultado. */
function groupLiteExtras(doc: Document): void {
  doc.querySelectorAll<HTMLElement>(".med-result").forEach((res) => {
    const grid = res.querySelector<HTMLElement>(".med-grid");
    if (!grid || grid.querySelector(":scope > details.lite-extra")) return;

    const extras = Array.from(grid.children).filter((c) =>
      c.matches('.med-card--safety, [class*="med-card--scenario"]')
    );
    if (extras.length === 0) return;

    const det = doc.createElement("details");
    det.className = "lite-extra";
    const sum = doc.createElement("summary");
    sum.textContent = "Cenário, cuidados e planejamento";
    det.appendChild(sum);
    extras.forEach((el) => {
      if (el.parentElement) liteHome.set(el, { pai: el.parentElement, antes: el.nextSibling });
      det.appendChild(el);
    });
    grid.appendChild(det);
  });
}

/* A nota de cenário do nível da seção (ex.: #continuaScenarioNote) é PERSISTENTE:
   o módulo só reescreve o innerHTML dela. Movê-la para dentro do resultado a
   destruía no primeiro recálculo — o elemento sumia do documento e o módulo
   nunca mais o encontrava. Aqui ela é recolhida NO LUGAR. */
function foldScenarioNotes(doc: Document): void {
  doc.querySelectorAll<HTMLElement>('section > .calc-result[id$="ScenarioNote"]').forEach((nota) => {
    if (nota.parentElement?.classList.contains("lite-note")) return;
    const det = doc.createElement("details");
    det.className = "lite-extra lite-note";
    const sum = doc.createElement("summary");
    sum.textContent = "Cenário do paciente";
    det.appendChild(sum);
    nota.parentElement?.insertBefore(det, nota);
    liteHome.set(nota, { pai: det.parentElement as Element, antes: det.nextSibling });
    det.appendChild(nota);
  });
}

/** Liga a apresentação LITE. Devolve a função que desliga. */
export function applyLitePresentation(doc: Document): () => void {
  injectLiteStyles(doc);
  doc.documentElement.classList.add("codex-lite");
  foldScenarioNotes(doc);
  groupLiteExtras(doc);

  /* setTimeout, nao requestAnimationFrame: rAF nao dispara quando o iframe nao
     esta compondo quadros (fora da tela, aba oculta), e ai o resultado voltaria
     a aparecer completo depois de um recalculo. */
  let agendado: ReturnType<typeof setTimeout> | null = null;
  const obs = new MutationObserver(() => {
    if (agendado) return;
    agendado = setTimeout(() => {
      agendado = null;
      groupLiteExtras(doc);
    }, 0);
  });
  const alvo = doc.querySelector("main") || doc.body;
  if (alvo) obs.observe(alvo, { childList: true, subtree: true });

  return () => {
    if (agendado) clearTimeout(agendado);
    obs.disconnect();
    /* O desmonte pode chegar com o iframe já em teardown: nós ficam órfãos e
       insertBefore lança NotFoundError, que quebrava o ModuleViewer inteiro ao
       sair do LITE. Restaurar é esforço melhor: se falhar, o iframe está indo
       embora de qualquer forma. */
    try {
      doc.documentElement.classList.remove("codex-lite");
      doc.querySelectorAll<HTMLElement>("details.lite-extra").forEach((det) => {
        const pai = det.parentElement;
        if (!pai) return;
        Array.from(det.children).forEach((c) => {
          if (c.tagName === "SUMMARY") return;
          const casa = liteHome.get(c);
          const destinoOk = casa && casa.pai.isConnected;
          const ancoraOk = destinoOk && (casa.antes === null || casa.antes.parentNode === casa.pai);
          if (ancoraOk) casa.pai.insertBefore(c, casa.antes);
          else if (destinoOk) casa.pai.appendChild(c);
          else pai.appendChild(c);
        });
        det.remove();
      });
    } catch {
      /* iframe já desmontado — nada a restaurar */
    }
  };
}
